import { CheckCircleOutlined, AccessTimeOutlined, ErrorOutline, SendOutlined } from '@mui/icons-material';

import type { ReviewStatus } from '@/types/adminOnboarding';

import { StepDataSummary } from '../StepDataSummary';

interface WaitingRoomStepProps {
  reviewStatus: ReviewStatus;
  rejectionMessage?: string;
  stepData: Record<number, Record<string, unknown>>;
  onConfirmSubmit: () => void;
  onBack: () => void;
  isSubmitting?: boolean;
}

export const WaitingRoomStep: React.FC<WaitingRoomStepProps> = ({
  reviewStatus,
  rejectionMessage,
  stepData,
  onConfirmSubmit,
  onBack,
  isSubmitting = false,
}) => {
  const isInProgress = reviewStatus === 'in_progress';
  const isPendingReview = reviewStatus === 'pending_review';
  const isApproved = reviewStatus === 'approved';
  const isRejected = reviewStatus === 'rejected';

  // ─── Summary + Confirm screen (user hasn't submitted yet) ─────────────────
  if (isInProgress) {
    return (
      <div className="space-y-6">
        <div className="text-center pb-2">
          <h3 className="text-lg font-semibold text-gray-900">Revisa tu información</h3>
          <p className="text-sm text-gray-500 mt-1">
            Antes de enviar tu solicitud, verifica que todos los datos sean correctos.
          </p>
        </div>

        <StepDataSummary stepData={stepData} />

        <div className="pt-2 border-t border-gray-100 flex flex-col gap-3">
          <button
            onClick={onConfirmSubmit}
            disabled={isSubmitting}
            className="w-full flex items-center justify-center gap-2 py-3 px-6 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60"
          >
            <SendOutlined fontSize="small" />
            {isSubmitting ? 'Enviando solicitud...' : 'Confirmar y enviar solicitud'}
          </button>
          <button
            onClick={onBack}
            disabled={isSubmitting}
            className="w-full py-2 px-6 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60"
          >
            Regresar al paso anterior
          </button>
          <p className="text-xs text-gray-400 text-center">
            Una vez enviada, tu solicitud quedará en revisión y recibirás una respuesta por email.
          </p>
        </div>
      </div>
    );
  }

  // ─── Waiting room (already submitted) ────────────────────────────────────
  return (
    <div className="space-y-6">
      <div
        className={`rounded-lg p-6 text-center ${
          isApproved
            ? 'bg-green-50 border border-green-200'
            : isRejected
              ? 'bg-red-50 border border-red-200'
              : 'bg-blue-50 border border-blue-200'
        }`}
      >
        {isApproved && (
          <>
            <CheckCircleOutlined className="text-green-500 mb-3" style={{ fontSize: 48 }} />
            <h3 className="text-lg font-semibold text-green-800">¡Solicitud aprobada!</h3>
            <p className="text-sm text-green-700 mt-2">
              Tu cuenta ha sido activada. Serás redirigido a tu panel en breve.
            </p>
          </>
        )}
        {isRejected && (
          <>
            <ErrorOutline className="text-red-500 mb-3" style={{ fontSize: 48 }} />
            <h3 className="text-lg font-semibold text-red-800">Se requieren correcciones</h3>
            <p className="text-sm text-red-700 mt-2">
              {rejectionMessage ?? 'Un super administrador ha solicitado cambios en tu solicitud.'}
            </p>
          </>
        )}
        {isPendingReview && (
          <>
            <AccessTimeOutlined className="text-blue-500 mb-3" style={{ fontSize: 48 }} />
            <h3 className="text-lg font-semibold text-blue-800">Solicitud en revisión</h3>
            <p className="text-sm text-blue-700 mt-2">
              Tu documentación ha sido enviada y está siendo revisada por nuestro equipo. Te
              notificaremos por email cuando haya una actualización.
            </p>
          </>
        )}
      </div>

      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">
          Resumen de la información enviada
        </h4>
        <StepDataSummary stepData={stepData} />
      </div>
    </div>
  );
};
